import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '@/components/DashboardLayout';
import FloatingFilterPanel from '@/components/FloatingFilterPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatUSD, formatINR } from '@/utils/formatters';
import { exportToPDF } from '@/utils/exportToPDF';
import { BarChart3, PieChart, TableIcon, ArrowUpDown, FileDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { parseISO, isValid, isWithinInterval, parse } from 'date-fns';

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
  '#F8B500', '#00CED1', '#FF7F50', '#6B5B95', '#88B04B'
];

// Parse date from various formats
const parseAnyDate = (dateStr) => {
  if (!dateStr) return null;
  
  let date = parseISO(dateStr);
  if (isValid(date)) return date;
  
  const formats = [
    'dd/MM/yyyy',
    'dd-MM-yyyy',
    'MM/dd/yyyy',
    'yyyy-MM-dd',
    'dd/MM/yyyy, HH:mm:ss',
    'dd-MM-yyyy HH:mm:ss'
  ];
  
  for (const fmt of formats) {
    try {
      date = parse(dateStr, fmt, new Date());
      if (isValid(date)) return date;
    } catch {}
  }
  
  date = new Date(dateStr);
  return isValid(date) ? date : null;
};

const OwnerFinance = () => {
  const { user } = useOutletContext();
  const [clientBillings, setClientBillings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryType, setCategoryType] = useState('task');
  const [sortOrder, setSortOrder] = useState('desc');
  const [chartType, setChartType] = useState('bar');
  const [dateFilter, setDateFilter] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('all');
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [billingsRes, summaryRes] = await Promise.all([
        api.get('/owner/client-billings'),
        api.get('/owner/financial-summary')
      ]);
      setClientBillings(billingsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading finance data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter billings based on date filter + month
  const filteredBillings = useMemo(() => {
    let result = clientBillings;
    
    // Month filter based on due_on date
    if (selectedMonth !== 'all') {
      const [fy, fm] = selectedMonth.split('-').map(Number);
      result = result.filter(b => {
        const d = b.due_on || b.file_received_on || '';
        if (!d) return false;
        const parsed = parseAnyDate(d);
        if (!parsed) return false;
        return parsed.getFullYear() === fy && (parsed.getMonth() + 1) === fm;
      });
    }
    
    if (dateFilter && dateFilter.startDate && dateFilter.endDate) {
      result = result.filter(b => {
        const dateValue = b[dateFilter.dateField];
        if (!dateValue) return false;
        const parsedDate = parseAnyDate(dateValue);
        if (!parsedDate) return false;
        return isWithinInterval(parsedDate, { start: dateFilter.startDate, end: dateFilter.endDate });
      });
    }
    
    return result;
  }, [clientBillings, dateFilter, selectedMonth]);
  
  // Calculate filtered summary
  const filteredSummary = useMemo(() => {
    const totalIncome = filteredBillings.reduce((sum, b) => sum + (b.amount || b.income || 0), 0);
    const billingCount = filteredBillings.length;
    const unacceptedCount = filteredBillings.filter(b => !b.resource_acceptance_status).length;
    const unacceptedAmount = filteredBillings
      .filter(b => !b.resource_acceptance_status)
      .reduce((sum, b) => sum + (b.amount || b.income || 0), 0);
    
    return {
      total_income: totalIncome,
      billing_count: billingCount,
      total_expenses: summary?.total_expenses || 0,
      total_tds: summary?.total_tds || 0,
      invoice_count: summary?.invoice_count || 0,
      unaccepted_count: unacceptedCount,
      unaccepted_amount: unacceptedAmount
    };
  }, [filteredBillings, summary]);
  
  const categorizedData = useMemo(() => {
    if (!filteredBillings.length) return [];
    
    const grouped = {};
    
    filteredBillings.forEach(billing => {
      let key;
      switch (categoryType) {
        case 'task':
          key = billing.task || 'Unknown';
          break;
        case 'client':
          key = billing.client || 'Unknown';
          break;
        case 'main_title':
          key = billing.main_title || 'Unknown';
          break;
        case 'target_language':
          key = billing.target_language || billing.subtitle_language || 'Unknown';
          break;
        case 'source_language':
          key = billing.source_language || 'Unknown';
          break;
        default:
          key = 'Unknown';
      }
      
      if (!grouped[key]) {
        grouped[key] = { name: key, count: 0, totalIncome: 0, totalDuration: 0 };
      }
      
      grouped[key].count += 1;
      grouped[key].totalIncome += billing.amount || billing.income || 0;
      grouped[key].totalDuration += billing.qty || billing.duration_words || 0;
    });
    
    let result = Object.values(grouped);
    result.sort((a, b) => sortOrder === 'desc' ? b.totalIncome - a.totalIncome : a.totalIncome - b.totalIncome);
    
    return result;
  }, [filteredBillings, categoryType, sortOrder]);
  
  const totalIncome = useMemo(() => 
    categorizedData.reduce((sum, item) => sum + item.totalIncome, 0),
    [categorizedData]
  );
  
  const profit = {
    amount: filteredSummary.total_income - (filteredSummary.total_expenses || 0),
    margin: filteredSummary.total_income > 0 
      ? ((filteredSummary.total_income - (filteredSummary.total_expenses || 0)) / filteredSummary.total_income * 100) 
      : 0
  };
  
  const categoryLabels = {
    'task': 'Task Type',
    'client': 'Client',
    'main_title': 'Project Title',
    'target_language': 'Target Language',
    'source_language': 'Source Language'
  };
  
  // Pie chart calculations
  const pieData = useMemo(() => {
    let cumulative = 0;
    return categorizedData.slice(0, 10).map((item, idx) => {
      const percentage = totalIncome > 0 ? (item.totalIncome / totalIncome) * 100 : 0;
      const startAngle = cumulative * 3.6;
      cumulative += percentage;
      return {
        ...item,
        percentage,
        startAngle,
        endAngle: cumulative * 3.6,
        color: COLORS[idx % COLORS.length]
      };
    });
  }, [categorizedData, totalIncome]);
  
  const handleExportPDF = async () => {
    toast.info('Generating PDF...');
    const success = await exportToPDF('finance-content', `finance-report-${new Date().toISOString().split('T')[0]}.pdf`);
    if (success) {
      toast.success('PDF downloaded successfully!');
    } else {
      toast.error('Failed to generate PDF');
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-96">
          <motion.div 
            className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout user={user}>
      <div id="finance-content" className="space-y-6">
        <motion.div 
          className="flex items-center justify-between"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <div className="text-center flex-1">
            <h1 className="text-4xl font-heading font-bold accent-gradient mb-2">Finance Analytics</h1>
            <p className="text-muted-foreground">Revenue breakdown and categorization</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {Array.from({ length: 12 }, (_, i) => {
                  const d = new Date(); d.setMonth(d.getMonth() - i);
                  const val = d.toISOString().slice(0, 7);
                  return <SelectItem key={val} value={val}>{d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button onClick={handleExportPDF} variant="outline" size="sm" className="bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200 text-rose-600 hover:from-rose-100 hover:to-pink-100 active:bg-red-600 active:text-white active:border-red-600 active:from-red-600 active:to-red-600" data-testid="finance-export-pdf-btn">
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </motion.div>
          </div>
        </motion.div>
        
        {/* Filter Active Indicator */}
        <AnimatePresence>
          {dateFilter && (
            <motion.div
              className="flex items-center gap-2 text-sm px-4 py-2.5 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Sparkles className="w-4 h-4 text-violet-500" />
              <span className="text-violet-700">
                Filter active: Showing <strong>{filteredBillings.length}</strong> of {clientBillings.length} records
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-6 text-center">
                <p className="text-sm font-medium text-muted-foreground mb-2">Total Income (Client)</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{formatUSD(filteredSummary.total_income)}</p>
                <p className="text-xs text-muted-foreground mt-1">{filteredSummary.billing_count} billings</p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.3 }}
          >
            <Card className="border-l-4 border-l-violet-500 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-6 text-center">
                <p className="text-sm font-medium text-muted-foreground mb-2">Total Expenses (Translators)</p>
                <p className="text-3xl font-bold text-violet-600">{formatINR(filteredSummary.total_expenses)}</p>
                <p className="text-xs text-muted-foreground mt-1">{filteredSummary.invoice_count} invoices</p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <Card className={`border-l-4 ${profit.amount >= 0 ? 'border-l-emerald-500' : 'border-l-orange-500'} hover:shadow-lg transition-shadow duration-300`}>
              <CardContent className="pt-6 text-center">
                <p className="text-sm font-medium text-muted-foreground mb-2">Net Profit (USD)</p>
                <p className={`text-3xl font-bold ${profit.amount >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                  {formatUSD(profit.amount)}
                </p>
              <p className="text-xs text-muted-foreground mt-1">{profit.margin.toFixed(1)}% margin</p>
            </CardContent>
          </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.3 }}
          >
            <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-6 text-center">
                <p className="text-sm font-medium text-muted-foreground mb-2">Pending Acceptance</p>
                <p className="text-3xl font-bold text-orange-600">{formatUSD(filteredSummary.unaccepted_amount)}</p>
                <p className="text-xs text-muted-foreground mt-1">{filteredSummary.unaccepted_count} invoices</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        {/* Analytics Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <CardTitle className="text-center md:text-left accent-gradient">Revenue by Category</CardTitle>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Select value={categoryType} onValueChange={setCategoryType}>
                    <SelectTrigger className="w-48 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="task">By Task Type</SelectItem>
                      <SelectItem value="client">By Client</SelectItem>
                      <SelectItem value="main_title">By Project Title</SelectItem>
                      <SelectItem value="target_language">By Target Language</SelectItem>
                    <SelectItem value="source_language">By Source Language</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                >
                  <ArrowUpDown className="w-4 h-4 mr-1" />
                  {sortOrder === 'desc' ? 'High to Low' : 'Low to High'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {categorizedData.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {dateFilter ? 'No billing data matches the selected filters.' : 'No billing data available. Import data in Client Billings tab.'}
                </p>
              </div>
            ) : (
              <Tabs value={chartType} onValueChange={setChartType} className="w-full">
                <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-6">
                  <TabsTrigger value="bar" className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Bar Chart
                  </TabsTrigger>
                  <TabsTrigger value="pie" className="flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Pie Chart
                  </TabsTrigger>
                  <TabsTrigger value="table" className="flex items-center gap-2">
                    <TableIcon className="w-4 h-4" />
                    Table
                  </TabsTrigger>
                </TabsList>
                
                {/* Bar Chart View */}
                <TabsContent value="bar">
                  <div className="space-y-4">
                    {categorizedData.slice(0, 15).map((item, idx) => {
                      const percentage = totalIncome > 0 ? (item.totalIncome / totalIncome) * 100 : 0;
                      return (
                        <div key={item.name} className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium truncate max-w-[250px]" title={item.name}>
                              {idx + 1}. {item.name}
                            </span>
                            <div className="flex items-center gap-4 text-right shrink-0 whitespace-nowrap">
                              <span className="text-muted-foreground">{item.count} records</span>
                              <span className="font-semibold text-success">{formatUSD(item.totalIncome)}</span>
                              <span className="text-muted-foreground w-14">{percentage.toFixed(1)}%</span>
                            </div>
                          </div>
                          <div className="h-3 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${percentage}%`,
                                backgroundColor: COLORS[idx % COLORS.length]
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {categorizedData.length > 15 && (
                      <p className="text-center text-sm text-muted-foreground pt-4">
                        Showing top 15 of {categorizedData.length} categories. Switch to Table view for all.
                      </p>
                    )}
                  </div>
                </TabsContent>
                
                {/* Pie Chart View */}
                <TabsContent value="pie">
                  <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
                    <div className="relative w-72 h-72">
                      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                        {pieData.map((item, idx) => {
                          const radius = 40;
                          const circumference = 2 * Math.PI * radius;
                          const strokeDasharray = (item.percentage / 100) * circumference;
                          const strokeDashoffset = pieData.slice(0, idx).reduce((acc, d) => acc + (d.percentage / 100) * circumference, 0);
                          
                          return (
                            <circle
                              key={item.name}
                              cx="50"
                              cy="50"
                              r={radius}
                              fill="transparent"
                              stroke={item.color}
                              strokeWidth="20"
                              strokeDasharray={`${strokeDasharray} ${circumference}`}
                              strokeDashoffset={-strokeDashoffset}
                              className="transition-all duration-500"
                            />
                          );
                        })}
                        <circle cx="50" cy="50" r="25" fill="white" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-primary">{categorizedData.length}</p>
                          <p className="text-xs text-muted-foreground">Categories</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {pieData.map((item, idx) => (
                        <div key={item.name} className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate max-w-[200px]" title={item.name}>
                              {item.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatUSD(item.totalIncome)} ({item.percentage.toFixed(1)}%)
                            </p>
                          </div>
                        </div>
                      ))}
                      {categorizedData.length > 10 && (
                        <p className="text-xs text-muted-foreground pt-2 border-t">
                          + {categorizedData.length - 10} more categories
                        </p>
                      )}
                    </div>
                  </div>
                </TabsContent>
                
                {/* Table View */}
                <TabsContent value="table">
                  <div className="overflow-x-auto max-h-[500px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background">
                        <TableRow>
                          <TableHead className="w-12 text-center">#</TableHead>
                          <TableHead>{categoryLabels[categoryType]}</TableHead>
                          <TableHead className="text-right">Records</TableHead>
                          <TableHead className="text-right">Duration</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Share</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categorizedData.map((item, idx) => (
                          <TableRow key={item.name}>
                            <TableCell className="text-center text-muted-foreground">{idx + 1}</TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-sm flex-shrink-0"
                                  style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                                />
                                <span className="truncate max-w-[250px]" title={item.name}>{item.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{item.count}</TableCell>
                            <TableCell className="text-right">{item.totalDuration.toFixed(1)}</TableCell>
                            <TableCell className="text-right font-semibold text-success">{formatUSD(item.totalIncome)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {totalIncome > 0 ? ((item.totalIncome / totalIncome) * 100).toFixed(1) : 0}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                
                {/* Total Summary */}
                <div className="flex justify-between items-center pt-6 border-t mt-6">
                  <span className="font-semibold">
                    Total ({categorizedData.length} {categoryLabels[categoryType].toLowerCase()}s)
                  </span>
                  <span className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{formatUSD(totalIncome)}</span>
                </div>
              </Tabs>
            )}
          </CardContent>
        </Card>
        </motion.div>
      </div>
      
      {/* Floating Filter Panel */}
      <FloatingFilterPanel onFilterChange={setDateFilter} showDateFieldSelector={true} />
    </DashboardLayout>
  );
};

export default OwnerFinance;
